import React, { type Dispatch, type SetStateAction } from 'react';

import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { type VariantProps } from 'class-variance-authority';
import { useIntl } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button, buttonVariants } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';

type TranslationEntry = {
    id: string;
    defaultMessage: string;
    values?: Record<string, string | number>;
};

type ConfirmationModalProps = {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    dialogDetails: {
        title: TranslationEntry;
        subTitle?: TranslationEntry;
        confirmationMessage: TranslationEntry;
        icon?: IconProp;
    };
    callbackButton: {
        variant: VariantProps<typeof buttonVariants>['variant'];
        callbackFn: () => void | Promise<void>;
        buttonTitle: TranslationEntry;
    };
    children?: React.ReactNode;
};
const ConfirmationModal = ({
                               isModalOpen,
                               setIsModalOpen,
                               dialogDetails,
                               callbackButton,
                               children,
                           }: ConfirmationModalProps) => {
    const { formatMessage } = useIntl();

    return (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-2">
                        {dialogDetails.icon && <SquareIcon icon={dialogDetails.icon} />}
                        <div>
                            <DialogTitle>
                                {formatMessage(
                                    { id: dialogDetails.title.id, defaultMessage: dialogDetails.title.defaultMessage },
                                    dialogDetails.title.values,
                                )}
                            </DialogTitle>
                            {dialogDetails?.subTitle && (
                                <DialogDescription>
                                    {formatMessage(
                                        {
                                            id: dialogDetails.subTitle.id,
                                            defaultMessage: dialogDetails.subTitle.defaultMessage,
                                        },
                                        dialogDetails.subTitle.values,
                                    )}
                                </DialogDescription>
                            )}
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        {formatMessage(
                            {
                                id: dialogDetails.confirmationMessage.id,
                                defaultMessage: dialogDetails.confirmationMessage.defaultMessage,
                            },
                            dialogDetails.confirmationMessage.values,
                        )}
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button variant={callbackButton.variant} onClick={callbackButton.callbackFn} className="flex-1">
                        {formatMessage(
                            {
                                id: callbackButton.buttonTitle.id,
                                defaultMessage: callbackButton.buttonTitle.defaultMessage,
                            },
                            callbackButton.buttonTitle.values,
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ConfirmationModal;